import React, { useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Switch,
  Upload,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  categoryItems,
  pageTypeItems,
  typeItems,
} from 'utils/handbooks/mainEventsHandbooks';
import { MainEventsService } from 'services/MainEventsService';
import {
  CREATE_ERROR,
  EDIT_ERROR,
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { useSelector, useDispatch } from 'react-redux';
import { requiredRule } from 'utils/forms/validationRules';
import { normFile } from 'utils/files';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import { fetchPackages } from 'store/reducers/staticHandbooksReducer';

function MainEventsForm({
  isActive,
  onClose,
  onSuccess,
  initialValues,
  pageNames,
}) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const isEditing = initialValues !== null;
  const [selectedCategory, setCategory] = useState(null);
  const [selectedType, setType] = useState(null);
  const { packagesData } = useSelector((state) => state.staticHandbooks);

  useEffect(() => {
    if (initialValues) {
      const {
        title,
        title_kz,
        description,
        description_kz,
        text,
        text_kz,
        button,
        button_kz,
        background_color,
        background_image,
        position,
        category,
        type,
        packages,
        page_url,
        page_type,
        is_published,
        authorized,
      } = initialValues;

      form.setFieldsValue({
        title,
        title_kz,
        description,
        description_kz,
        text,
        text_kz,
        button,
        button_kz,
        background_color,
        background_image: background_image
          ? [{ name: background_image }]
          : undefined,
        position,
        category,
        type,
        packages: packages?.filter((item) => item.id)?.map((item) => item.id),
        page_url,
        page_type,
        is_published,
        authorized,
      });

      setCategory(category);
      setType(type);
    } else {
      resetFields();
    }
  }, [initialValues]);

  useEffect(() => {
    form.setFieldsValue({
      type: selectedType,
    });
  }, [selectedCategory]);

  useEffect(() => {
    if (packagesData.items.length === 0) {
      dispatch(fetchPackages());
    }
  }, []);

  const handleSubmit = (values) => {
    const formData = new FormData();
    const payload = {
      ...values,
      category: selectedCategory,
      type: selectedType,
    };

    Object.entries(payload).forEach(([key, value]) => {
      if (key === 'is_published' || key === 'authorized') {
        formData.append(key, value);
        return;
      }

      if (key === 'packages') {
        payload?.packages?.forEach((item) => {
          formData.append('packages', +item);
        });
        return;
      }

      if (!value) {
        formData.append(key, '');
        return;
      }

      if (key === 'background_image') {
        if (value[0]?.originFileObj) {
          formData.append(key, value[0]?.originFileObj);
          return;
        }

        if (value[0]) {
          formData.append(key, value[0].name);
          return;
        }

        formData.append(key, '');
        return;
      }

      formData.append(key, value);
    });

    if (isEditing) {
      handleUpdate(formData, initialValues.id);
    } else {
      handleCreate(formData);
    }
  };

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await MainEventsService.createMainEvent(data);

      if (isCreated) {
        handleConfirm();
        message.success(SUCCESS_CREATE);
      }
    } catch (err) {
      message.error(CREATE_ERROR);
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await MainEventsService.updateMainEvent(data, id);

      if (isUpdated) {
        handleConfirm();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  const resetFields = () => {
    form.resetFields();
    setCategory(null);
    setType(null);
  };

  const handleConfirm = () => {
    onSuccess();
    onClose();
    resetFields();
  };

  return (
    <Drawer
      title={isEditing ? 'Редактирование события' : 'Создание события'}
      width="50%"
      onClose={onClose}
      visible={isActive}
      getContainer={false}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        labelAlign="right"
        initialValues={{
          is_published: true,
          authorized: false,
          background_color: '#000000',
        }}
      >
        <Form.Item name="category" label="Категория" rules={[requiredRule]}>
          <Select
            allowClear
            placeholder="Категория"
            onChange={(value) => {
              setCategory(value);
              setType(null);
            }}
          >
            {categoryItems?.map(({ text, value }) => (
              <Select.Option key={value} value={value}>
                {text}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {selectedCategory && (
          <Form.Item name="type" label="Тип" rules={[requiredRule]}>
            <Select
              allowClear
              placeholder="Тип"
              filterSort={(optionA, optionB) =>
                optionA.children
                  .toLowerCase()
                  .localeCompare(optionB.children.toLowerCase())
              }
              onChange={setType}
            >
              {typeItems[selectedCategory]?.map(({ text, value }) => (
                <Select.Option key={value} value={value}>
                  {text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item name="title" label="Заголовок" rules={[requiredRule]}>
          <Input placeholder="Введите заголовок" allowClear />
        </Form.Item>

        <Form.Item
          name="title_kz"
          label="Заголовок (каз.)"
          rules={[requiredRule]}
        >
          <Input placeholder="Введите заголовок (каз.)" allowClear />
        </Form.Item>

        <Form.Item name="description" label="Описание">
          <Input placeholder="Введите описание" allowClear />
        </Form.Item>

        <Form.Item name="description_kz" label="Описание (каз.)">
          <Input placeholder="Введите описание (каз.)" allowClear />
        </Form.Item>

        <Form.Item name="text" label="Текст">
          <Input placeholder="Введите текст" allowClear />
        </Form.Item>

        <Form.Item name="text_kz" label="Текст (каз.)">
          <Input placeholder="Введите текст (каз.)" allowClear />
        </Form.Item>

        <Form.Item name="button" label="Текст кнопки">
          <Input placeholder="Введите текст кнопки" allowClear />
        </Form.Item>

        <Form.Item name="button_kz" label="Текст кнопки (каз.)">
          <Input placeholder="Введите текст кнопки (каз.)" allowClear />
        </Form.Item>

        <Form.Item name="packages" label="Пакеты">
          <Select mode="multiple" placeholder="Пакеты" allowClear>
            {packagesData.items.map(({ id, title }) => (
              <Select.Option key={id} value={id}>
                {title}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="page_url" label="Page Name/URL">
          <Select allowClear placeholder="Название страницы">
            {pageNames?.map(({ key, value }) => (
              <Select.Option key={key} value={key}>
                {value}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="page_type" label="Page Type">
          <Select allowClear placeholder="Тип страницы">
            {pageTypeItems?.map(({ text, value }) => (
              <Select.Option key={value} value={value}>
                {text}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="background_color" label="Фоновый цвет">
          <Input
            type="color"
            style={{ width: '50%' }}
            placeholder="Введите фоновый цвет"
          />
        </Form.Item>

        <Form.Item
          name="background_image"
          label="Фоновая SVG картинка"
          valuePropName="fileList"
          getValueFromEvent={normFile}
        >
          <Upload accept=".svg" maxCount={1} beforeUpload={() => false}>
            <Button icon={<UploadOutlined />}>Загрузить</Button>
          </Upload>
        </Form.Item>

        <Form.Item name="position" label="Порядок" rules={[requiredRule]}>
          <InputNumber
            controls={false}
            placeholder="Введите порядок"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="authorized"
          label="Зарегистрирован"
          valuePropName="checked"
        >
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
          />
        </Form.Item>

        <Form.Item name="is_published" label="Статус" valuePropName="checked">
          <Switch
            defaultChecked
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
          />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { MainEventsForm };
